import { Modal } from 'antd';
import clsx from 'clsx';
import Image from 'next/image';
import React, { useEffect, useState } from 'react';
import { FaChevronRight } from 'react-icons/fa6';
import { IoClose, IoFlash } from 'react-icons/io5';

import { montserrat } from '@/components/Layout';

const STORAGE_KEY = 'zl_claim_free_book_modal_last_shown';
const TWENTY_FOUR_HOURS = 24 * 60 * 60 * 1000;

const ClaimFreeBookModals: React.FC = () => {
  const [isMounted, setIsMounted] = useState<boolean>(false);
  const [isBottomModalOpen, setIsBottomModalOpen] = useState<boolean>(false);
  const [isCenterModalOpen, setIsCenterModalOpen] = useState<boolean>(false);

  useEffect(() => {
    setIsMounted(true);
    try {
      const lastShown = localStorage.getItem(STORAGE_KEY);
      const now = Date.now();
      if (!lastShown || now - Number(lastShown) >= TWENTY_FOUR_HOURS) {
        setIsBottomModalOpen(true);
        localStorage.setItem(STORAGE_KEY, String(now));
      }
    } catch (err) {
      console.error('Error reading localStorage for free book modal:', err);
    }
  }, []);

  if (!isMounted) return null;

  const handleCloseBottomModal = () => {
    setIsBottomModalOpen(false);
  };

  const handleOpenCenterModal = () => {
    setIsBottomModalOpen(false);
    setIsCenterModalOpen(true);
  };

  const handleCloseCenterModal = () => {
    setIsCenterModalOpen(false);
  };

  return (
    <>
      {/* 1. Bottom-Right Modal */}
      <Modal
        open={isBottomModalOpen}
        onCancel={handleCloseBottomModal}
        footer={null}
        closable={false}
        mask={false}
        width={450}
        rootClassName="claim-free-book-bottom-modal-root"
        className="claim-free-book-bottom-modal"
        styles={{
          wrapper: {
            pointerEvents: 'none',
          },
          content: {
            backgroundColor: '#181818', // grayZl-925
            padding: 0,
            borderRadius: '12px',
            border: '1px solid #ffffff33', // grayZl-850
            overflow: 'hidden',
            pointerEvents: 'auto',
            fontFamily: montserrat.style.fontFamily,
            boxShadow:
              '0 20px 25px -5px rgba(0, 0, 0, 0.7), 0 8px 10px -6px rgba(0, 0, 0, 0.7)',
          },
        }}
      >
        <div
          className={clsx(
            montserrat.className,
            'relative flex items-stretch justify-between w-full overflow-hidden bg-grayZl-925 font-montserrat',
          )}
        >
          <div className='absolute top-0 left-0 w-full h-full'>
            <p className='text-[40px] text-white opacity-[2%] font-bold'>Create. Share. Get a Free Book.</p>
          </div>
          {/* Close button with semi-white background and black X icon */}
          <button
            onClick={handleCloseBottomModal}
            aria-label="Close"
            className="absolute top-2.5 right-2.5 w-5 h-5 rounded-full bg-white/50 hover:bg-white/40 flex items-center justify-center text-[#181818] transition-colors cursor-pointer z-30 shadow-sm"
          >
            <IoClose className="w-[14px] h-[14px] text-[#181818] stroke-[2.5]" />
          </button>

          {/* Left Text Column */}
          <div className="flex-1 flex-shrink-0 pl-[16px] pr-[16px] py-[16px] flex flex-col justify-between h-full z-10">
            <div>
              <h3 className="text-white font-semibold sm:text-[28px] text-[26px] tracking-tight leading-9">
                Create. Share. {" "}
                <span className="text-primary">
                  Get a Free Book.
                </span>
              </h3>

              <p className="text-grayZl-300 text-[12px] leading-[20px] mt-1 font-normal mb-3">
                Share your story on Instagram and tag{' '}
                <span className="text-grayZl-100 font-medium">@zebra_learn</span>.
              </p>
            </div>

            <button
              onClick={handleOpenCenterModal}
              className="bg-primary hover:bg-darkPrimary text-white text-[12px] font-semibold h-[32px] w-full rounded-lg flex items-center justify-center gap-1.5 transition-colors shadow-md cursor-pointer"
            >
              <span>View How to Claim</span>
              <FaChevronRight className="w-3 h-3 text-white" />
            </button>
          </div>

          {/* Right Image (Original Asset) */}
          <div className="relative flex-1 h-full flex-shrink-0 flex items-end justify-end overflow-hidden select-none pointer-events-none">
            <Image
              src="/assets/images/claim-book/brandology-phone.png"
              alt="Brandology Book with Phone"
              width={864}
              height={912}
              priority
              className="w-[212px] h-[228px] object-contain object-bottom-right"
            />
          </div>
        </div>
      </Modal>

      {/* 2. Center Modal */}
      <Modal
        open={isCenterModalOpen}
        onCancel={handleCloseCenterModal}
        footer={null}
        closable={false}
        centered
        width={358}
        rootClassName="claim-free-book-center-modal-root"
        className="claim-free-book-center-modal"
        styles={{
          content: {
            backgroundColor: '#181818', // grayZl-925
            padding: '16px',
            borderRadius: '12px',
            border: '1px solid #ffffff33', // grayZl-850
            overflow: 'hidden',
            fontFamily: montserrat.style.fontFamily,
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.85)',
          },
          mask: {
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            backdropFilter: 'blur(3px)',
          },
        }}
      >
        <div
          className={clsx(
            montserrat.className,
            'relative flex flex-col bg-grayZl-925 font-montserrat',
          )}
        >
          {/* Close button with semi-white background and black X icon */}
          <button
            onClick={handleCloseCenterModal}
            aria-label="Close"
            className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-white/50 hover:bg-white/40 flex items-center justify-center text-[#181818] transition-colors cursor-pointer z-30 shadow-sm"
          >
            <IoClose className="w-[14px] h-[14px]  text-[#181818] stroke-[2.5]" />
          </button>

          {/* Exclusive Offer Badge (Transparent background) */}
          <div className="w-fit inline-flex items-center  px-3 py-1 rounded-full border border-primary bg-transparent">
            <IoFlash className="w-4 h-4 text-primary" />
            <span className="text-primary text-[10px] leading-[18px] font-bold tracking-wider uppercase">
              EXCLUSIVE OFFER
            </span>
          </div>

          {/* Heading */}
          <h2 className="text-white text-[18px] leading-7 font-bold tracking-tight mt-2">
            Claim your <span className="text-primary">Free Book</span>
          </h2>

          {/* Numbered instructions */}
          <div className="mt-2 space-y-1.5">
            <p className="text-grayZl-25 text-[12px] leading-5">
              <span className="font-bold text-white">1.</span> Create a short video featuring your ZebraLearn book.
            </p>
            <p className="text-grayZl-25 text-[12px] leading-5">
              <span className="font-bold text-white">2.</span> Tag{' '}
              <span className="text-primary font-semibold">@Zebra_Learn</span> in a Reel or LinkedIn post only.
            </p>
          </div>

          {/* 2 Mockup images side by side (Original Assets) */}
          <div className="flex items-center justify-center gap-3.5 mt-3 mb-[26px] select-none">
            <div className="flex flex-col items-center">
              <Image
                src="/assets/images/claim-book/reel-mockup.png"
                alt="Instagram Reel Mockup"
                width={77}
                height={160}
                priority
                className="h-[185px] w-auto object-contain"
              />
            </div>
            <div className="flex flex-col items-center">
              <Image
                src="/assets/images/claim-book/linkedin-mockup.png"
                alt="LinkedIn Post Mockup"
                width={138}
                height={160}
                priority
                className="h-[185px] w-auto object-contain"
              />
            </div>
          </div>

          {/* Got it Button */}
          <button
            onClick={handleCloseCenterModal}
            className="h-[40px] w-full bg-primary hover:bg-darkPrimary text-white font-bold  flex items-center justify-center rounded-xl transition-colors cursor-pointer text-center text-[14px] shadow-lg"
          >
            Got it
          </button>

          {/* Footer note (tight space directly under Got it button) */}
          <p className="text-grayZl-500 text-[11px] text-center ">
            We&apos;ll check your tags to confirm your entry.
          </p>
        </div>
      </Modal>
    </>
  );
};

export default ClaimFreeBookModals;
